/**
 * Tenant branding — frontend source of truth.
 *
 * Every component that renders a company name, logo, tagline, policy
 * prefix, or primary color must pull from this module instead of
 * hardcoding "Zion Underwriting Partners" / "#1e3a5f" / etc.
 *
 * The defaults here match the launch tenant so the app still renders
 * correctly if the /api/branding call hasn't completed yet (e.g. first
 * paint of the login page). Once the BrandingProvider fetches the live
 * config from the backend it replaces the defaults.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const DEFAULT_BRANDING = {
  company_name: 'Zion Underwriting Partners LLC',
  short_name: 'Zion',
  tagline: 'Transportation Insurance Specialists',
  logo_filename: 'zion-logo.jpeg',
  logo_frontend_path: '/assets/zion-logo.jpeg',
  primary_color: '#1e3a5f',
  secondary_color: '#334155',
  accent_color: '#b91c1c',
  policy_prefix: 'ZUW',
  submission_prefix: 'ZUW',
  from_email: 'notifications@zionuw.com',
  from_name: 'Zion Underwriting Partners',
  support_email: 'support@zionuw.com',
  reply_to_email: 'support@zionuw.com',
  legal_entity_name: 'Zion Underwriting Partners LLC',
  legal_footer: 'Zion Underwriting Partners LLC | Transportation Insurance Specialists',
  underwritten_by_label: 'Zion Underwriting Partners LLC',
  public_domain: null,
};

const BrandingContext = createContext({
  branding: DEFAULT_BRANDING,
  tenantId: 'zion',
  loading: false,
  refresh: () => Promise.resolve(),
});

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [tenantId, setTenantId] = useState('zion');
  const [loading, setLoading] = useState(true);

  const fetchBranding = async () => {
    try {
      const resp = await axios.get(`${API_URL}/api/branding`);
      if (resp.data?.branding) {
        setBranding({ ...DEFAULT_BRANDING, ...resp.data.branding });
      }
      if (resp.data?.tenant_id) setTenantId(resp.data.tenant_id);
    } catch (err) {
      // Fail quiet — defaults above keep the app usable.
      // eslint-disable-next-line no-console
      console.warn('Branding fetch failed, using defaults.', err?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranding();
  }, []);

  // Expose the primary color as a CSS custom property so any component can
  // theme off `var(--brand-primary)` without a prop-drill.
  useEffect(() => {
    if (branding?.primary_color) {
      document.documentElement.style.setProperty('--brand-primary', branding.primary_color);
    }
    if (branding?.secondary_color) {
      document.documentElement.style.setProperty('--brand-secondary', branding.secondary_color);
    }
    if (branding?.accent_color) {
      document.documentElement.style.setProperty('--brand-accent', branding.accent_color);
    }
    // Session 215.82 — do NOT override document.title from branding.
    // Per-route SEO (see hooks/useSEO.js) is the single source of
    // truth for the tab title so /ntoa, /get-a-quote, etc. can
    // present their own keyword-rich titles for GSC indexing.
  }, [branding]);

  return (
    <BrandingContext.Provider
      value={{ branding, tenantId, loading, refresh: fetchBranding }}
    >
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}
